import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';
import { isDevAuthToken } from '../utils/devAuth';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const SUPPORT_CONTEXT_COOLDOWN_MS = 15000;

const supportContextTracker = {
  fetchKey: null,
  lastFetchAt: 0,
  inFlight: false,
};

/**
 * Support Context - Phase 8
 * 
 * Manages platform owner support context state:
 * - Agency context switching
 * - Client support mode
 * - Session tracking
 */
const SupportContext = createContext(null);

export const useSupportContext = () => {
  const context = useContext(SupportContext);
  if (!context) {
    // Return safe defaults if outside provider
    return {
      supportContext: null,
      isInSupportMode: false,
      isLoading: false,
      contextReady: true, // Default to ready when outside provider
      enterAgencyContext: () => Promise.resolve(),
      enterClientSupport: () => Promise.resolve(),
      exitSupportMode: () => Promise.resolve(),
      refreshContext: () => Promise.resolve(),
    };
  }
  return context;
};

export const SupportContextProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [supportContext, setSupportContext] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  // Track if initial context fetch has completed (important for route guards)
  const [contextReady, setContextReady] = useState(false);

  const isPlatformOwner = user?.role === 'platform_owner' || user?.role === 'super_admin';
  const isInSupportMode = supportContext?.is_support_mode || false;

  /**
   * Fetch current support context from backend.
   */
  const userId = user?.user_id;

  const refreshContext = useCallback(async ({ force = false } = {}) => {
    if (process.env.NODE_ENV === 'development') {
      console.count('[SupportContext] refreshContext called');
    }

    if (!token || !isPlatformOwner) {
      setSupportContext(null);
      setContextReady(true);
      return;
    }

    if (isDevAuthToken(token)) {
      setSupportContext(null);
      setContextReady(true);
      return;
    }

    const fetchKey = `${userId || ''}`;
    if (
      !force &&
      (supportContextTracker.inFlight ||
        (supportContextTracker.fetchKey === fetchKey &&
          Date.now() - supportContextTracker.lastFetchAt < SUPPORT_CONTEXT_COOLDOWN_MS))
    ) {
      return;
    }

    supportContextTracker.inFlight = true;
    supportContextTracker.fetchKey = fetchKey;
    setIsLoading(true);

    try {
      const response = await axios.get(`${API}/owner-console/context/current`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      setSupportContext(response.data);
    } catch (error) {
      console.error('Failed to fetch support context:', error);
      setSupportContext(null);
    } finally {
      supportContextTracker.inFlight = false;
      supportContextTracker.lastFetchAt = Date.now();
      setIsLoading(false);
      setContextReady(true);
    }
  }, [token, isPlatformOwner, userId]);

  /**
   * Enter agency context for support/admin.
   */
  const enterAgencyContext = useCallback(async (agencyId, reason = null, interactive = false) => {
    if (!token || !isPlatformOwner) {
      throw new Error('Platform owner access required');
    }

    setIsLoading(true);
    try {
      const response = await axios.post(
        `${API}/owner-console/context/enter-agency`,
        { agency_id: agencyId, reason, interactive },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      await refreshContext({ force: true });
      return response.data;
    } catch (error) {
      console.error('Failed to enter agency context:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [token, isPlatformOwner, refreshContext]);

  /**
   * Enter client support context to view their portal.
   */
  const enterClientSupport = useCallback(async (userId, reason = null, interactive = false) => {
    if (!token || !isPlatformOwner) {
      throw new Error('Platform owner access required');
    }

    setIsLoading(true);
    try {
      const response = await axios.post(
        `${API}/owner-console/context/support-client`,
        { user_id: userId, reason, interactive },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      await refreshContext({ force: true });
      return response.data;
    } catch (error) {
      console.error('Failed to enter client support:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [token, isPlatformOwner, refreshContext]);

  /**
   * Exit support mode and return to platform owner.
   */
  const exitSupportMode = useCallback(async () => {
    if (!token || !isPlatformOwner) return;

    setIsLoading(true);
    try {
      await axios.post(
        `${API}/owner-console/context/exit`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSupportContext(null);
      await refreshContext({ force: true });
    } catch (error) {
      console.error('Failed to exit support mode:', error);
    } finally {
      setIsLoading(false);
    }
  }, [token, isPlatformOwner, refreshContext]);

  const hasLoadedSupportContextRef = useRef(false);

  // Refresh once per stable actor identity; avoid resetting contextReady on every refetch.
  useEffect(() => {
    if (!token) {
      setSupportContext(null);
      setContextReady(true);
      hasLoadedSupportContextRef.current = false;
      return;
    }
    if (isPlatformOwner) {
      if (!hasLoadedSupportContextRef.current) {
        setContextReady(false);
      }
      refreshContext();
      hasLoadedSupportContextRef.current = true;
    } else {
      setSupportContext(null);
      setContextReady(true);
      hasLoadedSupportContextRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- refreshContext stable on scalar deps; avoid duplicate fetch on callback identity churn
  }, [token, isPlatformOwner, userId]);

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    supportContext,
    isInSupportMode,
    isLoading,
    contextReady, // Route guards should wait for this before redirecting
    isPlatformOwner,
    enterAgencyContext,
    enterClientSupport,
    exitSupportMode,
    refreshContext,
  }), [
    supportContext,
    isInSupportMode,
    isLoading,
    contextReady,
    isPlatformOwner,
    enterAgencyContext,
    enterClientSupport,
    exitSupportMode,
    refreshContext,
  ]);

  return (
    <SupportContext.Provider value={value}>
      {children}
    </SupportContext.Provider>
  );
};

export default SupportContext;
